"use client";

import { memo } from "react";
import { cn } from "@/lib/utils";
import Icon from "@/components/ui/Icon";
import { Typography } from "@/components/ui/typography";
import Image, { StaticImageData } from "next/image";

type CapabilitiesSectionProps = {
  title?: string;
  description?: string;
  imageUrl?: string | StaticImageData;
  imageDimensions?: {
    width: number;
    height: number;
  } | null;
  items?: string[];
} & React.ComponentProps<"section">;

const CapabilitiesSection = ({
  className,
  title,
  description,
  imageUrl,
  imageDimensions,
  items,
}: CapabilitiesSectionProps) => {
  return (
    <section className={cn("section bg-white py-12 lg:py-20", className)}>
      <div className="container mx-auto">
        <div className="grid grid-cols-1 gap-6 place-content-start lg:gap-12">
          <div className="grid grid-cols-1 gap-4 place-content-start md:gap-8 text-center">
            {!!title && (
              <Typography
                variant="heading4"
                className="lg:text-[32px] lg:leading-[150%] lg:text-grey-800"
              >
                <h2>{title}</h2>
              </Typography>
            )}
            {!!description && (
              <Typography
                variant="subHeadingSm"
                className="text-gray-600"
                asChild
              >
                <p>{description}</p>
              </Typography>
            )}
          </div>
          <div className="grid grid-cols-1 gap-6 place-content-start md:grid-cols-2 md:gap-6 lg:grid-cols-[minmax(0,0.4189fr)_minmax(0,0.5811fr)] lg:gap-[min(3rem,3.33vw)]">
            {!!imageUrl && (
              <div className="flex-auto min-w-0 rounded-2xl lg:rounded-lg overflow-hidden inline-flex aspect-1 sm:aspect-auto">
                <Image
                  src={imageUrl}
                  width={imageDimensions?.width ?? 449}
                  height={imageDimensions?.height ?? 300}
                  alt={title ?? 'Laguex illumination'}
                  className="flex-auto min-w-0 object-top object-cover"
                />
              </div>
            )}
            <div className="grid grid-cols-1 place-content-start gap-4 text-semantic-text-primary">
              {items?.map((description, i) => {
                return (
                  <div className="flex items-start" key={i}>
                    <Icon
                      id="icon-check-in-circle_32"
                      className="shrink-0 text-blue w-8 h-8 mr-4 md:text-[#1b1f3b] md:w-7 md:h-7 md:mr-2.5"
                    />
                    <Typography
                      variant="labelMd"
                      className="lg:text-[#1b1f3b] lg:text-base lg:leading-[150%] lg:font-medium"
                      asChild
                    >
                      <p>{description}</p>
                    </Typography>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default memo(CapabilitiesSection);
